import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Table, Alert, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import BaseSelect from '@/components/Elements/BaseSelect';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';

// https://chatgpt.com/c/69607710-a230-8323-8f9a-6d5cb2d54722

const PhoneDuplicateRelationModal = ({
    visible,
    duplicateData,
    onCancel,
    onConfirm,
    loading = false,
}) => {
    const [phoneFamilyMap, setPhoneFamilyMap] = useState({});

    const filterData = useSelector(state => state.filterReducer.list);

    const relationships = filterData.relationships;

    /** Init map khi mở modal */
    useEffect(() => {
        if (!visible) {
            setPhoneFamilyMap({});
            return;
        }

        const initialMap = Object.entries(duplicateData || {}).reduce(
            (acc, [phone, customers]) => {
                customers.forEach(c => {
                    acc[`${phone}_${c.customer_id}`] = {
                        phone,
                        related_customer_id: c.customer_id,
                        relationship_type: null,
                        relationship_type_inverse: null,
                    };
                });
                return acc;
            },
            {}
        );

        setPhoneFamilyMap(initialMap);
    }, [visible, duplicateData]);

    const tableData = useMemo(() => {
        return Object.entries(duplicateData || {}).flatMap(([phone, customers]) =>
            customers.map(c => {
                const key = `${phone}_${c.customer_id}`;
                return {
                    key,
                    phone,
                    customer_id: c.customer_id,
                    full_name: c.full_name,
                    customer_code: c.customer_code,
                    ...(phoneFamilyMap[key] || {}),
                };
            })
        );
    }, [duplicateData, phoneFamilyMap]);

    const hasInvalid = Object.values(phoneFamilyMap).some(
        r => !r.relationship_type || !r.relationship_type_inverse
    );

    const handleChange = (key, field, value) => {
        setPhoneFamilyMap(prev => ({
            ...prev,
            [key]: { ...prev[key], [field]: value },
        }));
    };

    const handleConfirm = () => {
        const phones = [...new Set(Object.values(phoneFamilyMap).map(r => r.phone))];

        const phonesHard = phones.map(phone => {
            const rel = Object.values(phoneFamilyMap).find(r => r.phone === phone);
            return {
                phone,
                confirmed_family: !!rel,
                related_customer_id: rel?.related_customer_id || null,
            };
        });

        const customerFamily = Object.values(phoneFamilyMap).map(r => ({
            related_id: r.related_customer_id,
            relationship_type: r.relationship_type,
            relationship_type_inverse: r.relationship_type_inverse,
        }));

        onConfirm({ phonesHard, customerFamily });
    };

    const columns = [
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>
                        {record.full_name || '—'}
                    </div>

                    <div style={{ fontSize: 12, color: '#888' }}>
                        {record.phone && <span>SĐT: {record.phone}</span>}
                        {record.customer_code && (
                            <span style={{ marginLeft: 12 }}>
                                Mâ: {record.customer_code}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: (
                <>
                    Khách hàng mới là
                    <Tooltip title="Mối quan hệ của khách hàng đang tạo đối với người này">
                        <InfoCircleOutlined style={{ marginLeft: 6 }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'relationship_type',
            key: 'relationship_type',
            width: 180,
            render: (value, record) => (
                <BaseSelect
                    options={relationships || []}
                    optionValue="id"
                    optionLabel="name"
                    placeholder="Chọn"
                    value={value ? value : null}
                    style={{ width: '100%' }}
                    showSearch={true}
                    onChange={val => handleChange(record.key, 'relationship_type', val)}
                />
            ),
        },
        {
            title: (
                <>
                    Người này là
                    <Tooltip title="Mối quan hệ của người này đối với khách hàng đang tạo">
                        <InfoCircleOutlined style={{ marginLeft: 6 }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'relationship_type_inverse',
            key: 'relationship_type_inverse',
            width: 180,
            render: (value, record) => (
                <BaseSelect
                    options={relationships || []}
                    optionValue="id"
                    optionLabel="name"
                    placeholder="Chọn"
                    value={value ? value : null}
                    style={{ width: '100%' }}
                    showSearch={true}
                    onChange={val =>
                        handleChange(record.key, 'relationship_type_inverse', val)
                    }
                />
            ),
        }
    ];

    return (
        <Modal
            title="Phát hiện số điện thoại đã được sử dụng"
            visible={visible}
            onCancel={onCancel}
            onOk={handleConfirm}
            confirmLoading={loading}
            okButtonProps={{ disabled: hasInvalid }}
            cancelText="Đóng"
            okText="Xác nhận"
            width={isMobile ? '100%' : 700}
            maskClosable={false}
        >
            <Alert
                type="warning"
                description={
                    <>
                        <div>
                            • Các số điện thoại sau <b>đã được liên kết với khách hàng khác</b> trong hệ thống:
                        </div>

                        <div style={{ margin: '6px 0 10px 16px' }}>
                            {Object.keys(duplicateData || {}).map(phone => (
                                <div key={phone}><strong>- {phone}</strong></div>
                            ))}
                        </div>
                        <div>• Số điện thoại <b>chỉ được dùng chung</b> nếu khách hàng có <b>mối quan hệ liên quan</b> (người thân, người giám hộ, người đại diện…).</div>
                        <div>• Nếu có mối quan hệ, vui lòng <b>chọn vai trò phù hợp</b> với khách hàng bên dưới.</div>
                        <div>
                            • Trường hợp <b>không có mối quan hệ liên quan</b>, bạn có thể <b>đóng popup và chỉnh sửa lại số điện thoại</b> để tiếp tục.
                        </div>
                    </>
                }
                style={{ marginBottom: 20 }}
            />

            <Table
                rowKey="key"
                columns={columns}
                dataSource={tableData}
                pagination={false}
                size="small"
                bordered
            />
        </Modal>
    );
};

export default PhoneDuplicateRelationModal;
